import React, { useEffect, useState } from 'react';
import { cleanString, validateSaveCustomClassDetails } from '../utils';
import { actions, inputs, overlays } from '@ecosystem/newron-design-system';
import { CustomSegmentType } from '../types';
import { getCustomSegmentsLabelDetails } from './customSegmentsUtils';

const { Modal } = overlays;
const { TextInput, TextArea } = inputs;
const { Button } = actions;

type Props = {
  customSegmentType: CustomSegmentType;
  isOpen: boolean;
  existingName: string[];
  onSave: (name: string, desc: string) => void;
  onClose: () => void;
};

/**
 * The modal component that is shown when the user press "Save"
 * in the Customization tray for Price Class and Feature Segmentation.
 */
export const SavedCustomSegmentsModal = ({ customSegmentType, isOpen, existingName, onSave, onClose }: Props) => {
  const customSegmentsLabelDetails = getCustomSegmentsLabelDetails()[customSegmentType];

  const [customSegmentsName, setCustomSegmentsName] = useState<string>('');
  const [customSegmentsDesc, setCustomSegmentsDesc] = useState<string>('');

  useEffect(() => {
    setCustomSegmentsName('');
    setCustomSegmentsDesc('');
  }, [isOpen]);

  /**
   * Calls the onSave callback
   * when the user press the save button
   */
  const handleSave = () => {
    onSave(customSegmentsName, customSegmentsDesc);
  };

  const isNameExisted = (): boolean => {
    return existingName.includes(customSegmentsName);
  };

  /**
   * Check the inserted name for the new price class/feature segment
   * the name must be unique and not empty
   * @returns {boolean} - Returns true if the save button should be disabled
   */
  const isSaveButtonDisabled = (): boolean => {
    return !validateSaveCustomClassDetails(customSegmentsName, customSegmentsDesc, isNameExisted);
  };

  const getErrorMessage = (): string | null => {
    const nameExistedErrorMessage = customSegmentsLabelDetails.errorMessage.nameExisted;

    return isNameExisted() ? nameExistedErrorMessage : null;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header>{customSegmentsLabelDetails.savedModalLabelText.header}</Modal.Header>
      <Modal.Body>
        <TextInput
          value={customSegmentsName}
          placeholder={customSegmentsLabelDetails.savedModalLabelText.placeholder}
          onChange={name => {
            setCustomSegmentsName(cleanString(name));
          }}
          maxLength={32}
          errorMessage={getErrorMessage()}
          testId="modal-save-segment-name"
        />
        <TextArea
          label="Description (optional)"
          placeholder=""
          onChange={desc => {
            setCustomSegmentsDesc(cleanString(desc));
          }}
          maxLength={64}
          value={customSegmentsDesc}
          testId="modal-save-segment-desc"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose}>Cancel</Button>
        <Button disabled={isSaveButtonDisabled()} onClick={handleSave} testId="modal-save-segment-btn">
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
